/**
 * 데이터형 게임 정의(카드) 호환 포맷 + 컴파일러 — 결정 #29. 공식 편집/교환 표면은
 * `.rqg` 게임 문서이고, GameDefinition은 그 문서를 런타임 GameModule(결정 #25)로
 * 변환하기 위한 내부 호환 입력이다. 임의 코드 실행이 전혀 없어(결정 #17/#27 화이트
 * 리스트) 주입 위험이 0이며, 순수 함수라 core에서 테스트로 고정된다(결정 #10).
 *
 * 네이티브 모듈(text-adventure 등)이 추상화를 "코드로" 실증했다면, 이 컴파일러는
 * 같은 추상화를 "데이터로" 채우는 길을 연다 — 제작 사다리(#29~#35)의 밑단.
 *
 * 보안 핵심:
 *  - 모델이 바꿀 수 있는 상태 키는 `mutableKeys` 화이트리스트로 제한된다. 선언되지
 *    않은 키를 건드리는 태그는 무시된다(#17 ambient authority 차단).
 *  - 종료 조건(endWhen)은 선언적 술어(key op value)뿐 — 임의 식 평가 없음.
 *  - 위젯은 정의의 바인딩으로만 생성되고, sanitizeViewSpec이 한 번 더 거른다(#27).
 */
import { rollDice, type StateOp, type TurnDiagnostic } from "./turn-protocol.js";
import type { ViewSpec, ViewWidget } from "./view-spec.js";
import { renderRoqeetText } from "../roqeet/index.js";
import { normalizeGameDefinition } from "./normalize-game-definition.js";
import type { RoqeetGameDocument } from "./roqeet-game-document.js";
import type {
  GameModule,
  GameModuleManifest,
  GameTurnInput,
  GameTurnOutcome,
} from "../ports/game-module.js";

/** 선언적 비교 연산자(결정 #29 종료 조건). 임의 코드 대신 화이트리스트 술어. */
export type GameConditionOp = "lte" | "gte" | "lt" | "gt" | "eq" | "neq" | "truthy" | "falsy";

/**
 * 선언적 조건 하나(결정 #29/#34). 상태 키를 연산자로 비교한다. 종료 조건·규칙
 * 트리거가 공유하는 기본 술어 — 임의 식 평가가 전혀 없다(주입 위험 0).
 */
export interface GameCondition {
  key: string;
  op: GameConditionOp;
  /** 비교값(truthy/falsy는 불필요). */
  value?: number | string | boolean;
}

/**
 * 규칙 발동 시 적용할 상태 변경(결정 #34). 작성자가 정의하므로 mutableKeys
 * 화이트리스트와 무관하게 어떤 키든 쓸 수 있다(모델이 직접 못 건드리는 파생 상태).
 */
export interface GameRuleAction {
  op: "set" | "inc" | "dec";
  key: string;
  value: number | string | boolean;
}

/**
 * 조건부 규칙(결정 #34: 블록 코딩의 데이터 토대). 매 턴 상태 갱신 후, when의 모든
 * 조건이 참이면(AND) then의 상태 변경을 적용한다. 화이트리스트 연산만 쓰는 선언적
 * 규칙이라 안전하며(#17), 폼·코드·블록 모드가 같은 데이터를 공유한다.
 *
 * 단일 패스로 위에서 아래로 한 번만 평가한다(결정적, 무한 루프 없음). 한 규칙의
 * 결과가 아래 규칙의 조건에 반영되므로 순서가 의미를 가진다.
 */
export interface GameRule {
  /** 발동 조건(모두 참이어야 함). 빈 배열이면 항상 발동(상수 효과). */
  when: GameCondition[];
  /** 발동 시 적용할 상태 변경. */
  then: GameRuleAction[];
}

/**
 * 조건부 프롬프트 주입 게이트(갭2 — `GameRule`의 자매). `rules`가 "조건 → 상태 변경"
 * 이라면, gate는 "조건 → 프롬프트 조각 주입"이다. 매 턴 `protocolInstructions`를 만들 때
 * when의 모든 조건이 참이면(AND) inject 문구를 지시문에 덧붙인다. inject는
 * stateSummaryTemplate과 동일하게 `{{key}}`를 현재 상태값으로 치환한다.
 *
 * 용도(anti-drift의 정답): "호감도 < 300이면 [지인 미만] 티어로서 허용 행동은 냉랭한
 * 예의뿐"처럼, **상태가 결정한 행동 규범을 모델에게 '이미 결정된 지시'로** 건넨다.
 * 모델은 판단하지 않고 그 지시를 따른다(ASK→PREVENT 전환). `rules`와 달리 상태를
 * 바꾸지 않는다(읽기 전용) — 순수하게 프롬프트 구성에만 관여한다.
 */
export interface GameGate {
  /** 발동 조건(모두 참이어야 함). 빈 배열이면 항상 주입(상수 지시). */
  when: GameCondition[];
  /** 발동 시 지시문에 덧붙일 프롬프트 조각. `{{key}}` 치환 지원. */
  inject: string;
}

/** 한 종료 조건(결정 #29). 매칭되면 게임이 끝나고 endingLabel/알림이 정해진다. */
export interface GameEndCondition {
  /** 검사할 상태 키. */
  key: string;
  op: GameConditionOp;
  /** 비교값(truthy/falsy는 불필요). */
  value?: number | string | boolean;
  /** 엔딩 라벨(UI 표시). */
  label?: string;
  /** 종료 알림 배너 문구(viewSpec). */
  notice?: string;
  /** 알림 톤. */
  tone?: "info" | "success" | "warning" | "danger";
}

/** 수치 클램프(결정 #28 상태 정합). 매 턴 적용 후 범위로 가둔다. */
export interface GameClamp {
  key: string;
  min?: number;
  max?: number;
}

export type GameStateValueType = "number" | "string" | "boolean" | "list" | "array" | "string[]" | "object" | "enum";
export type GameStateMutableBy = "ai" | "engine" | "readonly";

/**
 * 공식 `.rqg`의 상태 스키마 힌트. `initialState`가 실제 시작값이고 이 스키마는
 * 타입/범위/mutable/derived 계약을 명시한다. 파생 상태는 rules/initRules가 산출하고,
 * LLM이 직접 쓰지 못하게 mutable=false로 둔다.
 */
export interface GameStateFieldSchema {
  key: string;
  valueType: GameStateValueType;
  default?: number | string | boolean | unknown[] | Record<string, unknown>;
  min?: number;
  max?: number;
  values?: string[];
  mutableBy?: GameStateMutableBy;
  mutable?: boolean;
  derived?: boolean;
  transient?: boolean;
  label?: string;
}

/**
 * 엔티티 필드 기본값(갭1 지연 구체화). 새 엔티티(NPC)가 처음 관측될 때 이 필드들로
 * 시작 상태가 채워진다. 예: { aff: 0, open: 0, pride: 0, ten: 50 }. 다중 NPC 게임에서
 * 모든 인물이 공유하는 스탯 스키마를 정의한다.
 */
export type GameEntityTemplate = Record<string, number | string | boolean>;

/**
 * 알려진 엔티티별 정적/초기 속성(갭1). 이름 → 필드맵. 관측 시 템플릿 위에 덮어쓴다
 * (예: 박아영은 faction="학생회", pride=900). 여기 없는 이름도 템플릿만으로 관측될 수
 * 있다(완전 동적 NPC). 시작 상태에 미리 깔지 않으므로 토큰 비용 0 — 관측될 때만
 * `state.entities`에 구체화된다(슈뢰딩거 NPC).
 */
export type GameEntityRoster = Record<string, Record<string, number | string | boolean>>;

/** 주사위 판정 규칙(결정 #4 재현성). dcKey가 있으면 결과를 그 난이도와 비교. */
export interface GameDiceRule {
  /** 굴림 결과를 저장할 키(기본 "lastRoll"). */
  resultKey?: string;
  /** 표기를 저장할 키(기본 "lastRollNotation"). */
  notationKey?: string;
  /** 난이도 키(있으면 성공/실패 판정). 1회용 — 판정 후 소거. */
  dcKey?: string;
  /** 성공 여부를 저장할 키(기본 "lastRollSuccess"). */
  successKey?: string;
}

/** 스탯 바 바인딩(결정 #27). 값/최대를 상태 키에서 읽어 막대로. */
export interface GameStatBarBinding {
  label: string;
  valueKey: string;
  /** 최대값을 담은 상태 키(우선) 또는 고정 max. */
  maxKey?: string;
  max?: number;
  tone?: string;
}

/** 스탯 패널 항목 바인딩(결정 #27). 라벨:값(상태 키). */
export interface GameStatPanelBinding {
  label: string;
  key: string;
}

export interface GameHudDeclaration {
  statBars?: GameStatBarBinding[];
  statPanel?: GameStatPanelBinding[];
  inventoryKey?: string;
  inventoryTitle?: string;
  inventoryTitleKey?: string;
}

export interface GameAssetRegistryEntry {
  key: string;
  kind: "image" | "audio" | "sfx" | "video" | "other";
  /** Local asset id, package-relative key, or future resolver handle. */
  assetId?: string;
  label?: string;
}

export interface GameAssetBinding {
  id: string;
  slot: "background" | "sprite" | "bgm" | "sfx" | "overlay";
  /** Prefer assetKey for package/preset portability; assetId remains allowed for local cards. */
  assetKey?: string;
  assetId?: string;
  when?: GameCondition[];
  characterId?: string;
  emotion?: string;
  position?: "left" | "center" | "right";
}

/** viewSpec 선언(결정 #27). 어떤 위젯을 어떤 상태 키로 그릴지. */
export interface GameViewBinding {
  /** HUD에 표시할 스탯 바들. */
  statBars?: GameStatBarBinding[];
  /** HUD에 표시할 스탯 패널(라벨:값 묶음). */
  statPanel?: GameStatPanelBinding[];
  /** 인벤토리(배열) 상태 키. */
  inventoryKey?: string;
  inventoryTitle?: string;
  inventoryTitleKey?: string;
  /** state.choices를 선택지 위젯으로 노출할지(기본 true). */
  showChoices?: boolean;
  /** 주사위 결과(resultKey)를 위젯으로 노출할지. */
  showDice?: boolean;
}

/** setup 필드의 한 선택지(갭3). value는 상태에 병합될 값, label은 UI 표시용. */
export interface GameSetupOption {
  value: string;
  label?: string;
}

/**
 * 세션 시작 시 플레이어에게 묻는 setup 필드 하나(갭3). 선택값은 `key`로 초기 상태에
 * 병합된다(예: player_gender=M). 선언된 필드 키만 병합되므로 임의 키 주입이 차단된다
 * (#17). 잘못된/누락된 선택은 default(없으면 첫 옵션)로 폴백한다.
 */
export interface GameSetupField {
  /** 초기 상태에 병합될 키. */
  key: string;
  /** UI 라벨(질문 문구). */
  label?: string;
  /** 선택지 목록(최소 1개 권장). */
  options: GameSetupOption[];
  /** 기본 선택값(미지정이면 첫 옵션). 잘못된 입력의 폴백. */
  default?: string;
}

export interface GameSceneChoice {
  id?: string;
  label: string;
  to?: string;
  when?: GameCondition[];
  effects?: GameRuleAction[];
}

export interface GameScene {
  id: string;
  title?: string;
  content: string;
  choices?: GameSceneChoice[];
}

export interface GameSceneGraph {
  startSceneId?: string;
  scenes: GameScene[];
}

/**
 * 데이터형 게임 정의(결정 #29). `.rqg` 게임 문서가 이 모양으로 낮아져 등록된다.
 * 모든 동작은 선언적 — 임의 코드가 없다.
 */
export interface GameDefinition {
  /** 안정적 식별자(인스턴스가 아닌 이름). 예: "escape-room". */
  id: string;
  title: string;
  titleKey?: string;
  description?: string;
  descriptionKey?: string;
  icon?: string;
  /** 세션 시작 시의 초기 상태(직렬화 가능, 결정 #28). */
  initialState: Record<string, unknown>;
  /**
   * 세션 시작 시 플레이어에게 묻는 선택 필드(갭3). 선택값은 선언된 key로 초기 상태에
   * 병합된다(예: 성별·소속). 그 뒤 initRules가 파생 상태를 산출한다. 미지정이면 기존처럼
   * 고정 초기 상태로 시작한다.
   */
  setup?: GameSetupField[];
  /**
   * AI 서술/연출 가이드. 게임 규칙 집행 주체가 아니며, 규칙·상태 변경 권한·종료 조건은
   * rules/clamps/endWhen/view 같은 선언 노드가 맡는다. 모델 출력은 제안 action이고,
   * 엔진이 validate/apply한다.
   */
  instructions: string;
  instructionsKey?: string;
  /**
   * 지시문 끝에 붙일 현재 상태 요약 템플릿. `{{key}}` 자리표시자를 현재 상태값으로
   * 치환한다(없으면 빈 문자열). 예: "현재 HP: {{hp}}/{{maxHp}}, 위치: {{location}}".
   */
  stateSummaryTemplate?: string;
  stateSummaryTemplateKey?: string;
  /** Scene graph lowered from `.rqg` game.scene nodes for runtime preview and playground integrations. */
  sceneGraph?: GameSceneGraph;
  /** 공식 상태 스키마. 시작값은 initialState, 실행 중 파생은 rules/initRules가 담당한다. */
  stateSchema?: GameStateFieldSchema[];
  /**
   * 모델이 태그로 변경할 수 있는 상태 키 화이트리스트(결정 #17). 지정 시 목록 밖
   * 키를 건드리는 stateDelta는 무시된다. 미지정(undefined)이면 모든 키 허용(주의).
   */
  mutableKeys?: string[];
  /** 매 턴 적용 후 수치 클램프(결정 #28). */
  clamps?: GameClamp[];
  /**
   * 엔티티(NPC) 필드 기본 스키마(갭1 지연 구체화). 지정 시 다중 엔티티 게임이 된다.
   * `entity=` 속성이 붙은 stateDelta가 들어오면, 해당 이름이 아직 없을 때 이 템플릿
   * (+entityRoster의 정적 속성)으로 `state.entities[name]`을 구체화한다(슈뢰딩거 NPC).
   */
  entityTemplate?: GameEntityTemplate;
  /**
   * 알려진 엔티티별 정적/초기 속성(갭1). 관측 시 entityTemplate 위에 덮어쓴다. 여기
   * 없는 이름도 템플릿만으로 구체화될 수 있다(완전 동적 NPC). 시작 상태에 미리 깔지
   * 않으므로 토큰 0 — 첫 관측 때만 state.entities에 들어간다.
   */
  entityRoster?: GameEntityRoster;
  /**
   * 모델이 엔티티 필드 중 바꿀 수 있는 것의 화이트리스트(결정 #17, 엔티티판). 지정 시
   * 목록 밖 필드를 건드리는 entity stateDelta는 무시된다. 미지정이면 모든 엔티티 필드
   * 허용. 파생 필드(예: tier)는 여기서 빼고 rules로만 산출하게 한다.
   */
  mutableEntityKeys?: string[];
  /** 매 턴 적용 후 모든 엔티티 필드에 일괄 적용할 수치 클램프(갭1, 예: aff 0~1000). */
  entityClamps?: GameClamp[];
  /**
   * 조건부 규칙(결정 #34). 매 턴 상태 갱신 후 위에서 아래로 한 번 평가해, 조건이
   * 맞으면 파생 상태를 바꾼다(예: HP<20이면 status=위급). 화이트리스트 연산만.
   */
  rules?: GameRule[];
  /**
   * 세션 시작 시 1회만 평가하는 파생 규칙(갭3). setup 선택값이 병합된 직후 위에서
   * 아래로 한 번 적용된다. `rules`(매 턴)와 달리 초기 상태 구성에만 쓰인다 — 예:
   * player_gender=M이면 difficulty="hard"처럼 선택에 따른 시작 조건을 파생한다.
   */
  initRules?: GameRule[];
  /**
   * 조건부 프롬프트 주입 게이트(갭2). 매 턴 지시문을 구성할 때 현재 상태로 평가해,
   * when이 모두 참인 gate의 inject 문구를 지시문 끝(상태 요약 뒤)에 순서대로 덧붙인다.
   * 상태를 바꾸지 않는 읽기 전용 — 상태가 결정한 행동 규범을 모델에 '이미 결정된
   * 지시'로 건네 drift를 막는다(ASK→PREVENT). inject는 `{{key}}` 치환을 지원한다.
   */
  gates?: GameGate[];
  /** 주사위 판정 규칙(있으면 첫 굴림을 처리, 결정 #4). */
  dice?: GameDiceRule;
  /** 종료 조건(하나라도 매칭되면 종료, 결정 #29). 위에서부터 먼저 매칭된 것이 이긴다. */
  endWhen?: GameEndCondition[];
  /** 패키지/프리셋이 제공하는 에셋 레지스트리. 렌더링은 assetBindings가 상태 조건으로 결정한다. */
  assets?: GameAssetRegistryEntry[];
  /** AI 토큰 출력이 아니라 상태와 조건으로 background/sprite/audio slot을 결정한다. */
  assetBindings?: GameAssetBinding[];
  /** HUD 전용 선언. 기존 view.statBars/statPanel과 병합되어 렌더링된다. */
  hud?: GameHudDeclaration;
  /** viewSpec 바인딩(결정 #27). */
  view?: GameViewBinding;
}

/** `@(key)` RoQeet 치환자와 legacy `{{key}}` 자리표시자를 상태값으로 치환한다. */
function interpolate(template: string, state: Record<string, unknown>): string {
  const legacyRendered = template.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_full, key: string) => {
    const v = state[key];
    if (v === undefined || v === null) return "";
    if (Array.isArray(v)) return v.map((x) => String(x)).join(", ");
    return String(v);
  });
  return renderRoqeetText(legacyRendered, { state, values: state });
}

/** 한 조건을 현재 상태에 대해 평가한다(선언적 술어). 종료 조건·규칙이 공유. */
function evalCondition(
  cond: { key: string; op: GameConditionOp; value?: number | string | boolean },
  state: Record<string, unknown>,
): boolean {
  const cur = state[cond.key];
  switch (cond.op) {
    case "truthy":
      return !!cur;
    case "falsy":
      return !cur;
    case "eq":
      return cur === cond.value;
    case "neq":
      return cur !== cond.value;
    case "lte":
      return toNum(cur) <= toNum(cond.value);
    case "gte":
      return toNum(cur) >= toNum(cond.value);
    case "lt":
      return toNum(cur) < toNum(cond.value);
    case "gt":
      return toNum(cur) > toNum(cond.value);
    default:
      return false;
  }
}

/**
 * 조건부 규칙을 한 번 평가해 적용한다(결정 #34). 위에서 아래로 단일 패스 — 한 규칙의
 * 결과가 아래 규칙 조건에 반영된다. when이 모두 참이면 then의 상태 변경을 적용한다.
 * 작성자 정의 규칙이므로 mutableKeys 화이트리스트와 무관하다(파생 상태 산출용).
 */
function applyRules(state: Record<string, unknown>, rules: GameRule[]): Record<string, unknown> {
  let next = state;
  for (const rule of rules) {
    const fires = (rule.when ?? []).every((c) => evalCondition(c, next));
    if (!fires) continue;
    next = { ...next };
    for (const action of rule.then ?? []) {
      switch (action.op) {
        case "set":
          next[action.key] = action.value;
          break;
        case "inc":
          next[action.key] = toNum(next[action.key]) + toNum(action.value);
          break;
        case "dec":
          next[action.key] = toNum(next[action.key]) - toNum(action.value);
          break;
      }
    }
  }
  return next;
}

function schemaClamps(schema: GameStateFieldSchema[] | undefined): GameClamp[] {
  return (schema ?? [])
    .filter((field) => field.min !== undefined || field.max !== undefined)
    .map((field) => ({
      key: field.key,
      ...(field.min !== undefined ? { min: field.min } : {}),
      ...(field.max !== undefined ? { max: field.max } : {}),
    }));
}

function applyClampsToState(
  state: Record<string, unknown>,
  clamps: GameClamp[] | undefined,
): Record<string, unknown> {
  if (!clamps?.length) return state;
  const next = { ...state };
  for (const c of clamps) {
    if (next[c.key] === undefined) continue;
    let n = toNum(next[c.key]);
    if (c.min !== undefined) n = Math.max(c.min, n);
    if (c.max !== undefined) n = Math.min(c.max, n);
    next[c.key] = n;
  }
  return next;
}

function mergeViewBinding(
  view: GameViewBinding | undefined,
  hud: GameHudDeclaration | undefined,
): GameViewBinding {
  if (!hud) return view ?? {};
  return {
    ...(view ?? {}),
    statBars: [...(hud.statBars ?? []), ...(view?.statBars ?? [])],
    statPanel: [...(hud.statPanel ?? []), ...(view?.statPanel ?? [])],
    inventoryKey: view?.inventoryKey ?? hud.inventoryKey,
    inventoryTitle: view?.inventoryTitle ?? hud.inventoryTitle,
    inventoryTitleKey: view?.inventoryTitleKey ?? hud.inventoryTitleKey,
  };
}

function resolveAssetRef(
  binding: GameAssetBinding,
  assetsByKey: Map<string, GameAssetRegistryEntry>,
): string | undefined {
  if (binding.assetId) return binding.assetId;
  if (!binding.assetKey) return undefined;
  return assetsByKey.get(binding.assetKey)?.assetId ?? binding.assetKey;
}

function activeAssetBindings(def: GameDefinition, state: Record<string, unknown>): GameAssetBinding[] {
  return (def.assetBindings ?? []).filter((binding) =>
    (binding.when ?? []).every((condition) => evalCondition(condition, state)),
  );
}

/** 문자열 태그 값을 number/boolean으로 자연 변환(엔티티 필드용). */
function coerceValue(value: string | undefined): unknown {
  if (value === undefined) return true; // 베어 플래그
  if (value === "true") return true;
  if (value === "false") return false;
  if (value !== "" && Number.isFinite(Number(value))) return Number(value);
  return value;
}

/**
 * 엔티티(NPC) 대상 상태 연산을 적용한다(갭1 지연 구체화). `entity` 속성이 붙은
 * StateOp만 처리한다. 대상 엔티티가 아직 `state.entities`에 없으면 template + roster
 * (이름별 정적 속성)로 구체화한다 — 이것이 "관측"(슈뢰딩거 NPC). 그 뒤 화이트리스트
 * (allowEntity)로 거른 필드만 변경하고, 마지막에 entityClamps를 모든 엔티티에 적용한다.
 *
 * 순수 함수: 새 객체를 반환한다(입력 불변). 엔티티 op도 없고 클램프도 없으면 원본을
 * 그대로 돌려준다(평면 게임은 비용 0 — 가산성).
 */
function applyEntityDelta(
  state: Record<string, unknown>,
  ops: StateOp[],
  opts: {
    template?: GameEntityTemplate;
    roster?: GameEntityRoster;
    allowEntity?: Set<string>;
    clamps?: GameClamp[];
  },
): Record<string, unknown> {
  const entityOps = ops.filter((o) => o.entity);
  const hasClamps = !!opts.clamps?.length;
  if (entityOps.length === 0 && !hasClamps) return state;

  const prev =
    state["entities"] && typeof state["entities"] === "object"
      ? (state["entities"] as Record<string, Record<string, unknown>>)
      : {};
  const entities: Record<string, Record<string, unknown>> = { ...prev };

  for (const op of entityOps) {
    const name = op.entity!;
    // 구체화: 없으면 template + roster로 생성(첫 관측). 있으면 복사해 불변 유지.
    if (!entities[name]) {
      entities[name] = {
        ...(opts.template ?? {}),
        ...(opts.roster?.[name] ?? {}),
        observed: true,
      };
    } else {
      entities[name] = { ...entities[name] };
    }
    // 화이트리스트 밖 필드는 변경 무시(엔티티는 관측되었으되 불법 변경은 차단, 결정 #17).
    if (opts.allowEntity && !opts.allowEntity.has(op.key)) continue;
    const ent = entities[name]!;
    switch (op.op) {
      case "set":
        ent[op.key] = coerceValue(op.value);
        break;
      case "inc":
        ent[op.key] = toNum(ent[op.key]) + toNum(op.value);
        break;
      case "dec":
        ent[op.key] = toNum(ent[op.key]) - toNum(op.value);
        break;
      case "push": {
        const arr = Array.isArray(ent[op.key]) ? [...(ent[op.key] as unknown[])] : [];
        arr.push(coerceValue(op.value));
        ent[op.key] = arr;
        break;
      }
      case "unset":
        delete ent[op.key];
        break;
    }
  }

  // entityClamps: 지정된 필드를 모든 엔티티에서 범위로 가둔다(예: aff 0~1000).
  if (hasClamps) {
    for (const name of Object.keys(entities)) {
      let changed = false;
      const ent = { ...entities[name]! };
      for (const c of opts.clamps!) {
        if (ent[c.key] === undefined) continue;
        let n = toNum(ent[c.key]);
        if (c.min !== undefined) n = Math.max(c.min, n);
        if (c.max !== undefined) n = Math.min(c.max, n);
        if (ent[c.key] !== n) {
          ent[c.key] = n;
          changed = true;
        }
      }
      if (changed) entities[name] = ent;
    }
  }

  return { ...state, entities };
}

function engineActionContract(def: GameDefinition): string {
  const keys = def.mutableKeys?.length ? def.mutableKeys.join(", ") : "all top-level state keys (legacy mode)";
  const entityKeys = def.mutableEntityKeys?.length ? def.mutableEntityKeys.join(", ") : "declared entity fields";
  const lines = [
    "RoQeet Engine action contract:",
    "- You write narration and may propose actions; the engine, not the model, validates and applies game rules.",
    "- Put proposed actions after the narration as RoQeet command lines only when needed.",
    "- Invalid, incomplete, or unauthorized actions are ignored and reported by the engine.",
    `- AI can propose changes to these keys: ${keys}. The engine validates and applies allowed changes.`,
    "- Supported actions: @choices { ... }, @set key = value, @inc key amount, @dec key amount, @push key value, @unset key.",
    "- Visual/procedure actions: @scene key=value and @roll d20 dc=12.",
  ];
  if (def.entityTemplate || def.entityRoster || def.mutableEntityKeys?.length) {
    lines.push(`- Entity actions must include 대상=name or entity=name and may change only: ${entityKeys}.`);
  }
  return lines.join("\n");
}

/**
 * 데이터형 게임 정의를 런타임 GameModule(결정 #25)로 컴파일한다(결정 #29). 순수
 * 함수 — 같은 정의는 같은 모듈을 만든다. 반환된 모듈은 네이티브 모듈과 동일한
 * 계약을 만족하므로, 레지스트리·전송 파이프라인·viewSpec 렌더러가 구분 없이 다룬다.
 */
export function compileGameDefinition(defInput: GameDefinition | RoqeetGameDocument): GameModule {
  const def = normalizeGameDefinition(defInput);
  const manifest: GameModuleManifest = {
    id: def.id,
    title: def.title,
    ...(def.titleKey !== undefined ? { titleKey: def.titleKey } : {}),
    kind: "data",
    ...(def.description !== undefined ? { description: def.description } : {}),
    ...(def.descriptionKey !== undefined ? { descriptionKey: def.descriptionKey } : {}),
    ...(def.icon !== undefined ? { icon: def.icon } : {}),
  };

  const stateSchemaByKey = new Map(def.stateSchema.map((field) => [field.key, field]));
  const allowEntity = def.mutableEntityKeys ? new Set(def.mutableEntityKeys) : undefined;
  const diceResultKey = def.dice?.resultKey ?? "lastRoll";
  const diceNotationKey = def.dice?.notationKey ?? "lastRollNotation";
  const diceSuccessKey = def.dice?.successKey ?? "lastRollSuccess";
  const effectiveClamps = def.statePermissions.clamps.length
    ? def.statePermissions.clamps
    : [...schemaClamps(def.stateSchema), ...(def.clamps ?? [])];
  const assetsByKey = new Map((def.assets ?? []).map((asset) => [asset.key, asset]));

  const initialState = (setup?: Record<string, string>): Record<string, unknown> => {
    let state = clone(def.initialState);

    for (const field of def.stateSchema ?? []) {
      if (state[field.key] === undefined && field.default !== undefined) {
        state[field.key] = clone(field.default);
      }
    }

    // 1) setup 선택값 병합(갭3). 선언된 필드 키만, 유효한 옵션값만 반영한다(#17 임의 키
    //    주입 차단). 누락/부정 선택은 default(없으면 첫 옵션)로 폴백한다.
    if (def.setup?.length) {
      for (const field of def.setup) {
        const valid = new Set(field.options.map((o) => o.value));
        const fallback = field.default ?? field.options[0]?.value;
        const picked = setup?.[field.key];
        const value = picked !== undefined && valid.has(picked) ? picked : fallback;
        if (value !== undefined) state[field.key] = value;
      }
    }

    // 2) initRules 1회 평가(갭3). setup 병합 직후 파생 상태를 산출한다(예: 선택에 따른
    //    시작 난이도/분기 플래그). 매 턴 도는 rules와 달리 세션 시작 시에만 적용된다.
    if (def.initRules?.length) {
      state = applyRules(state, def.initRules);
    }

    return applyClampsToState(state, effectiveClamps);
  };

  const protocolInstructions = (state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): string => {
    // 서술 가이드 → 엔진 action 계약 → 상태 요약 → 조건부 게이트 주입(갭2) 순서로
    // 쌓는다. 게이트는
    // 상태가 결정한 행동 규범을 '이미 결정된 지시'로 모델에 건네 drift를 막는다.
    const tf = (k: string, fallback: string) => t ? t(k) : fallback;
    const instructions = def.instructionsKey ? tf(def.instructionsKey, def.instructions) : def.instructions;
    const parts: string[] = [];
    if (instructions.trim()) parts.push(instructions);
    parts.push(engineActionContract(def));

    if (def.stateSummaryTemplate) {
      const tmpl = def.stateSummaryTemplateKey ? tf(def.stateSummaryTemplateKey, def.stateSummaryTemplate) : def.stateSummaryTemplate;
      const summary = interpolate(tmpl, state).trim();
      if (summary) parts.push(summary);
    }

    for (const gate of def.gates ?? []) {
      const fires = (gate.when ?? []).every((c) => evalCondition(c, state));
      if (!fires) continue;
      const text = interpolate(gate.inject ?? "", state).trim();
      if (text) parts.push(text);
    }

    return parts.join("\n\n");
  };

  const applyTurn = (input: GameTurnInput, t?: (k: string, ...args: any[]) => string): GameTurnOutcome => {
    const diagnostics: TurnDiagnostic[] = [...(input.parsed.diagnostics ?? [])];

    // 1) 평면(전역) 상태 연산: entity 속성이 없는 것만, 화이트리스트로 거른다(결정 #17).
    //    entity 속성이 붙은 연산은 평면 경로에서 제외하고 아래 엔티티 단계에서 처리한다.
    const flatOps = input.parsed.stateDelta.filter((o: StateOp) => !o.entity);
    let state = applyValidatedStateOps(input.state, flatOps, stateSchemaByKey, diagnostics);

    // 1.5) 엔티티(NPC) 연산 + 지연 구체화(갭1). entity 속성이 붙은 연산을 처리하고,
    //      관측되지 않은 NPC는 template+roster로 구체화한다. entityClamps도 여기서 적용.
    if (def.entityTemplate || def.entityRoster || def.mutableEntityKeys || def.entityClamps?.length) {
      if (allowEntity) {
        for (const op of input.parsed.stateDelta.filter((o: StateOp) => o.entity)) {
          if (!allowEntity.has(op.key)) {
            diagnostics.push({
              severity: "warning",
              code: "invalid_action",
              message: `Entity action "${op.op}" for "${op.key}" is not allowed by mutableEntityKeys.`,
              source: `${op.op} ${op.key} entity=${op.entity}`,
            });
          }
        }
      }
      state = applyEntityDelta(state, input.parsed.stateDelta, {
        ...(def.entityTemplate ? { template: def.entityTemplate } : {}),
        ...(def.entityRoster ? { roster: def.entityRoster } : {}),
        ...(allowEntity ? { allowEntity } : {}),
        ...(def.entityClamps ? { clamps: def.entityClamps } : {}),
      });
    }

    // 2) 주사위 판정(결정 #4). 규칙이 있고 굴림이 왔을 때만.
    if (def.dice && input.parsed.rolls.length > 0) {
      const first = input.parsed.rolls[0]!;
      const result = rollDice(first.notation, input.random);
      const next: Record<string, unknown> = {
        ...state,
        [diceResultKey]: result,
        [diceNotationKey]: first.notation,
      };
      const dcKey = def.dice.dcKey ?? "";
      const dc = typeof first.dc === "number"
        ? first.dc
        : dcKey && typeof next[dcKey] === "number"
          ? (next[dcKey] as number)
          : undefined;
      if (typeof dc === "number") {
        next[diceSuccessKey] = result >= dc;
        delete next[dcKey]; // 1회용 난이도 소거(다음 판정 누수 방지).
      } else {
        delete next[diceSuccessKey];
      }
      state = next;
    }

    // 3) 클램프(결정 #28).
    state = applyClampsToState(state, effectiveClamps);

    // 4) 조건부 규칙(결정 #34). 갱신된 상태로 파생 상태를 산출한다(예: HP<20→status=위급).
    //    종료 조건보다 먼저 돌려, 규칙이 만든 상태를 종료 조건이 검사할 수 있게 한다.
    if (def.rules?.length) {
      state = applyRules(state, def.rules);
    }

    // 5) 선택지 영속(결정 #28): viewSpec이 순수 함수가 되도록.
    state = { ...state, choices: input.parsed.choices };

    // 6) 종료 조건 평가(결정 #29). 먼저 매칭된 조건이 엔딩을 정한다.
    let ended = state["ended"] === true;
    let endingLabel: string | undefined =
      typeof state["ending"] === "string" ? (state["ending"] as string) : undefined;
    for (const cond of def.endWhen ?? []) {
      if (evalCondition(cond, state)) {
        ended = true;
        endingLabel = cond.label ?? endingLabel ?? "엔딩";
        state = {
          ...state,
          ended: true,
          ending: endingLabel,
          ...(cond.notice ? { endingNotice: cond.notice } : {}),
          ...(cond.tone ? { endingTone: cond.tone } : {}),
        };
        break;
      }
    }

    const outcome: GameTurnOutcome = {
      state,
      ended,
      ...(diagnostics.length ? { diagnostics } : {}),
    };
    if (ended && endingLabel) outcome.endingLabel = t ? t("game.endingEnd", endingLabel) : endingLabel;
    return outcome;
  };

  const viewSpec = (state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): ViewSpec => {
    const v = mergeViewBinding(def.view, def.hud);
    const hud: ViewWidget[] = [];
    
    const tf = (k: string, fallback: string) => t ? t(k) : fallback;

    for (const bar of v.statBars ?? []) {
      const value = toNum(state[bar.valueKey]);
      const max =
        bar.maxKey && state[bar.maxKey] !== undefined ? toNum(state[bar.maxKey]) : (bar.max ?? 100);
      hud.push({
        kind: "stat-bar",
        label: bar.label,
        value: Math.max(0, value),
        max: max > 0 ? max : 1,
        ...(bar.tone ? { tone: bar.tone } : {}),
      });
    }

    if (v.statPanel?.length) {
      hud.push({
        kind: "stat-panel",
        stats: v.statPanel.map((p) => ({
          label: p.label,
          value: formatStatValue(state[p.key]),
        })),
      });
    }

    const widgets: ViewWidget[] = [];
    const ended = state["ended"] === true;

    const activeAssets = activeAssetBindings(def, state);
    if (activeAssets.length) {
      const background = [...activeAssets].reverse().find((binding) => binding.slot === "background");
      const sprites = activeAssets
        .filter((binding) => binding.slot === "sprite")
        .map((binding) => ({
          ...(resolveAssetRef(binding, assetsByKey)
            ? { assetId: resolveAssetRef(binding, assetsByKey) }
            : {}),
          ...(binding.emotion ? { emotion: binding.emotion } : {}),
          ...(binding.characterId ? { characterId: binding.characterId } : {}),
          ...(binding.position ? { position: binding.position } : {}),
        }));
      const backgroundAssetId = background ? resolveAssetRef(background, assetsByKey) : undefined;
      if (backgroundAssetId || sprites.length) {
        widgets.push({
          kind: "sprite-stage",
          ...(backgroundAssetId ? { backgroundAssetId } : {}),
          sprites,
        });
      }
    }

    if (v.showDice && state[diceResultKey] != null) {
      const dice: ViewWidget = {
        kind: "dice",
        notation: String(state[diceNotationKey] ?? "d20"),
        result: toNum(state[diceResultKey]),
      };
      if (typeof state[diceSuccessKey] === "boolean") dice.success = state[diceSuccessKey] as boolean;
      widgets.push(dice);
    }

    if (v.inventoryKey) {
      const items = Array.isArray(state[v.inventoryKey])
        ? (state[v.inventoryKey] as unknown[]).map((x) => ({ name: String(x) }))
        : [];
      if (items.length > 0) {
        widgets.push({
          kind: "inventory",
          ...(v.inventoryTitle ? { title: v.inventoryTitleKey ? tf(v.inventoryTitleKey, v.inventoryTitle) : v.inventoryTitle } : {}),
          items,
        });
      }
    }

    if (ended) {
      widgets.push({
        kind: "notice",
        tone: (state["endingTone"] as "info" | "success" | "warning" | "danger") ?? "info",
        text: String(state["endingNotice"] ?? `이야기가 끝났습니다 — ${String(state["ending"] ?? "엔딩")}`),
      });
    } else if (v.showChoices !== false) {
      const choices = Array.isArray(state["choices"])
        ? (state["choices"] as unknown[]).map((x) => String(x))
        : [];
      if (choices.length > 0) widgets.push({ kind: "choices", choices });
    }

    return { widgets, hud };
  };

  return { manifest, initialState, protocolInstructions, applyTurn, viewSpec };
}

/** stat-panel 값 포맷: 숫자/문자는 그대로, 배열은 join, 그 외는 String. */
function applyValidatedStateOps(
  state: Record<string, unknown>,
  ops: StateOp[],
  schemaByKey: Map<string, GameStateFieldSchema>,
  diagnostics: TurnDiagnostic[],
): Record<string, unknown> {
  let next: Record<string, unknown> = { ...state };
  for (const op of ops) {
    const field = schemaByKey.get(op.key);
    if (!field) {
      pushInvalidAction(diagnostics, op, `State action "${op.op}" for unknown key "${op.key}" is not allowed.`);
      continue;
    }
    if (field.mutableBy !== "ai" || field.derived) {
      pushInvalidAction(
        diagnostics,
        op,
        `State action "${op.op}" for "${op.key}" is not allowed because the field is ${field.derived ? "derived/engine-controlled" : field.mutableBy}.`,
      );
      continue;
    }

    const applied = applyValidatedStateOp(next, op, field, diagnostics);
    if (applied) next = applied;
  }
  return next;
}

function applyValidatedStateOp(
  state: Record<string, unknown>,
  op: StateOp,
  field: GameStateFieldSchema,
  diagnostics: TurnDiagnostic[],
): Record<string, unknown> | undefined {
  const valueType = field.valueType === "list" ? "array" : field.valueType;
  switch (op.op) {
    case "set": {
      const value = coerceForField(op.value, field);
      if (!isAllowedFieldValue(value, field)) {
        pushInvalidAction(diagnostics, op, `State action "set" for "${op.key}" has an invalid ${field.valueType} value.`);
        return undefined;
      }
      return { ...state, [op.key]: value };
    }
    case "inc":
    case "dec": {
      if (valueType !== "number") {
        pushInvalidAction(diagnostics, op, `State action "${op.op}" for "${op.key}" is only valid for number fields.`);
        return undefined;
      }
      const amount = Number(op.value);
      if (!Number.isFinite(amount)) {
        pushInvalidAction(diagnostics, op, `State action "${op.op}" for "${op.key}" has an invalid numeric amount.`);
        return undefined;
      }
      const current = toNum(state[op.key]);
      return { ...state, [op.key]: op.op === "inc" ? current + amount : current - amount };
    }
    case "push": {
      if (valueType !== "array" && valueType !== "string[]") {
        pushInvalidAction(diagnostics, op, `State action "push" for "${op.key}" is only valid for array fields.`);
        return undefined;
      }
      const item = valueType === "string[]" ? String(op.value ?? "") : coerceValue(op.value);
      const arr = Array.isArray(state[op.key]) ? [...(state[op.key] as unknown[])] : [];
      arr.push(item);
      return { ...state, [op.key]: arr };
    }
    case "unset":
      return Object.fromEntries(Object.entries(state).filter(([key]) => key !== op.key));
  }
}

function coerceForField(value: string | undefined, field: GameStateFieldSchema): unknown {
  const valueType = field.valueType === "list" ? "array" : field.valueType;
  switch (valueType) {
    case "number":
      return Number(value);
    case "boolean":
      if (value === "true") return true;
      if (value === "false") return false;
      return value;
    case "array":
    case "string[]":
      if (!value) return [];
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    case "enum":
    case "string":
    case "object":
    default:
      return value ?? "";
  }
}

function isAllowedFieldValue(value: unknown, field: GameStateFieldSchema): boolean {
  const valueType = field.valueType === "list" ? "array" : field.valueType;
  switch (valueType) {
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "boolean":
      return typeof value === "boolean";
    case "enum":
      return typeof value === "string" && (!field.values?.length || field.values.includes(value));
    case "string":
      return typeof value === "string";
    case "string[]":
      return Array.isArray(value) && value.every((item) => typeof item === "string");
    case "array":
      return Array.isArray(value);
    case "object":
      return !!value && typeof value === "object" && !Array.isArray(value);
  }
}

function pushInvalidAction(diagnostics: TurnDiagnostic[], op: StateOp, message: string): void {
  diagnostics.push({
    severity: "warning",
    code: "invalid_action",
    message,
    source: `${op.op} ${op.key}`,
  });
}

function formatStatValue(v: unknown): string | number {
  if (typeof v === "number" || typeof v === "string") return v;
  if (Array.isArray(v)) return v.map((x) => String(x)).join(", ");
  if (v === undefined || v === null) return "-";
  return String(v);
}

/** 직렬화 가능한 상태의 깊은 복사(결정 #28: state는 JSON 직렬화 가능). */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function toNum(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}
